import React, { Dispatch, SetStateAction, useState } from "react";

import { DataStructure } from "@/components/StaticTable";
import { Filter, FiltersPayloads } from "@/types/ApiResponse";
import { FiltersPopin } from "@/components/StaticTable/FiltersPopin";
import Popin from "@/components/Popin";
import { useTranslation } from "react-i18next";
import { FiltersSection } from "@/components/StaticTable/FiltersSection";
import { Operator } from "@/components/StaticTable/Filters/operators";

interface DefaultFilterPopinData {
  defaultKey?: string;
  defaultOperateur?: Operator;
  defaultValeur?: string | number | Date;
  defaultValeurMin?: string | number | Date;
  defaultValeurMax?: string | number | Date;
}

interface FiltersProps<T> {
  dataStructure: DataStructure<T>[];
  filters: FiltersPayloads;
  setFilters: Dispatch<SetStateAction<FiltersPayloads>>;
  colonneSelectionneePourFiltre: DataStructure<T> | null;
  setColonneSelectionneePourFiltre: React.Dispatch<
    React.SetStateAction<DataStructure<T> | null>
  >;
}

export function Filters<T>({
  dataStructure,
  filters,
  setFilters,
  colonneSelectionneePourFiltre,
  setColonneSelectionneePourFiltre,
}: FiltersProps<T>) {
  const { t } = useTranslation();

  const [defaultFilterPopinData, setDefaultFilterPopinData] =
    useState<DefaultFilterPopinData | null>(null);

  const updateFilters = (updatedFilters: Filter[]) => {
    if (setFilters) setFilters(updatedFilters as FiltersPayloads);
    setColonneSelectionneePourFiltre(null);
  };

  const onAddNewFiltre = (newFilter: Filter) => {
    if (!filters) return updateFilters([newFilter]);

    const newFilters = filters.filter((filter) => {
      return filter.key !== newFilter.key;
    });

    return updateFilters([...newFilters, newFilter]);
  };

  const onEditFiltre = (oldFilter: Filter, newFilter: Filter) => {
    if (!filters || !newFilter || !oldFilter) return;

    const newFilters = filters.filter((filter) => {
      return filter.key !== oldFilter.key;
    });

    newFilters.push(newFilter);

    updateFilters(newFilters);
  };

  const onFiltrePopinClose = () => {
    setDefaultFilterPopinData(null);
    setColonneSelectionneePourFiltre(null);
  };

  return (
    <>
      <FiltersSection
        dataStructure={dataStructure}
        filters={filters}
        setFilters={setFilters}
        setColonneSelectionneePourFiltre={setColonneSelectionneePourFiltre}
        setDefaultFilterPopinData={setDefaultFilterPopinData}
      />
      <Popin
        title={t("common.dmri.filter.title")}
        open={!!colonneSelectionneePourFiltre}
        content={
          <FiltersPopin<T>
            dataStructure={dataStructure}
            onAddNewFiltre={(newfiltre) => {
              onAddNewFiltre(newfiltre);
              setDefaultFilterPopinData(null);
            }}
            onEditFiltre={(oldFilter, newFiltre) => {
              onEditFiltre(oldFilter, newFiltre);
              setDefaultFilterPopinData(null);
            }}
            selectedcolumn={colonneSelectionneePourFiltre as DataStructure<T>}
            defaultKey={defaultFilterPopinData?.defaultKey}
            defaultOperateur={defaultFilterPopinData?.defaultOperateur}
            defaultValeur={defaultFilterPopinData?.defaultValeur}
            defaultValeurMin={defaultFilterPopinData?.defaultValeurMin}
            defaultValeurMax={defaultFilterPopinData?.defaultValeurMax}
          />
        }
        onClose={onFiltrePopinClose}
        maxWidth="md"
        showActionsButtons={false}
      />
    </>
  );
}
